// @ts-nocheck
import { http, HttpResponse, delay } from 'msw';
import {
	passwordRulesMockData,
	securityQuestionsMockData,
	usernameRulesMockData,
} from './mockData';
import { mfaPhoneNumberHandlers } from './mfaPhoneNumberHandlers';
import { mockSettings, mockSettingsDefaults } from './mockSettings';

export const validZipCode = '12345';
export const validDateOfBirth = '2024-09-01';

const isTestEnv = () => process.env.NODE_ENV === 'test';

const dynamicDelay = () => (isTestEnv() ? 1 : mockSettings.networkDelay);
/**
 * Updates mock settings by first restoring the defaults and then applying only the
 * requested changes.
 * @param {Object} settings The settings to apply to the mock settings
 * @return {Null}
 */
export function updateMockSettings(settings) {
	Object.keys(mockSettingsDefaults).forEach(key => {
		mockSettings[key] =
			settings[key] === undefined ? mockSettingsDefaults[key] : settings[key];
	});
}

/**
 * This is the old, complicated and brittle handler. It should be removed
 * once all stories are updated to the new handler.
 */
function legacyUserVerificationHandler(request, body) {
	const { checkZipCode, checkDOB } = mockSettings;
	const firstName = (body.firstName || '').toLowerCase();
	const token = (body.token || '').toLowerCase();
	const registrationToken = (body.registrationToken || '').toLowerCase();
	const lastName = (body.lastName || '').toLowerCase();
	const email = (body.email || '').toLowerCase();
	const userId = (body.userId || '').toLowerCase();
	const password = (body.password || '').toLowerCase();
	const pin = (body.pin || '').toLowerCase();
	const taxIdNumber = (body.taxIdNumber || '').toLowerCase();
	const stepOneToken = '';

	const response = {
		token, // jwt token format
		status: 'valid',
		memberInfo: {
			// unique identifier for mapping member creation
			// email: "tomjohn@tj.com", // only for sub-account to display in step 2 of registration
			clinicName: "St. Vincent's Hospital Outpatient PT",
			clinicAddress: {
				address1: '1124 Oak St.',
				address2: null,
				city: 'San Diego',
				state: 'CA',
				zip: '92127',
			},
			providerName: 'John Smith',
		},
	};
	if (
		(registrationToken && mockSettings.isASHLinkRegistration) ||
		mockSettings.returnPrimaryAccountError
	) {
		const userType = mockSettings.isASHLinkFitnessUserType
			? 'fitnessUser'
			: 'clinicalUser';
		const ashLinkResponse = {
			token: registrationToken,
			status: mockSettings.returnPrimaryAccountError ? 'invalid' : 'valid',
			invalidCode: mockSettings.returnPrimaryAccountError
				? 'PrimaryAccountNotRegistered'
				: null,
			displayInfo:
				userType === 'fitnessUser'
					? {
							location: "Bob's Gym",
							address: '1124 Oak St. San Diego, CA 92127',
					  }
					: { name: 'John Smith' },

			registrationUserType: userType,
			isPrimary: mockSettings.isASHLinkPrimaryUser,
			email: mockSettings.isASHLinkPrimaryUser
				? null
				: 'subAccountEmail@test.com', // only for sub-account to display in step 2 of registration
		};
		return HttpResponse.json(ashLinkResponse);
	}
	if (mockSettings.showAccountExistsGenericErrorOnRoleSubmit) {
		response.eligibilityStatusCode = 'alreadyExist';
		response.status = 'alreadyexist';
		return HttpResponse.json(response);
	}
	if (email === 'existingemail@gmail.com') {
		response.eligibilityStatusCode = 'existingemail';
		response.status = 'existingemail';

		return HttpResponse.json(response);
	}
	if (lastName === 'test') {
		if (firstName === 'clientexists') {
			response.eligibilityStatusCode = 'alreadyExist';
			response.status = 'alreadyExist';
		} else if (firstName === 'duplicate') {
			response.eligibilityStatusCode = 'requiredmoreInfo';
			response.status = 'requiredmoreInfo';
		} else {
			response.eligibilityStatusCode = 'invalid';
			response.status = 'invalid';
		}

		if (firstName === 'clientvalid') {
			response.eligibilityStatusCode = 'valid';
			response.status = 'valid';
			response.token = 'abcd.sdada.wqwerrwe';
		}
	} else if (
		(userId === 'validuser' &&
			password === 'validpassword' &&
			pin === '1234') ||
		(taxIdNumber === '1234' && email === 'validemail@gmail.com')
	) {
		response.eligibilityStatusCode = 'valid';
		response.status = 'valid';
		response.token = 'abcd.sdada.wqwerrwe';
	} else {
		response.eligibilityStatusCode = 'other';
		response.status = 'other';
	}

	if (firstName === '400user') {
		return HttpResponse.json(
			{
				responseStatus: {
					message: "'Client Id' should not be empty.",
					stackTrace: null,
					errors: [
						{
							fieldName: 'ClientId',
							message: "'Client Id' should not be empty.",
						},
						{
							fieldName: 'FirstName',
							message: "'First Name' should not be empty.",
						},
						{
							fieldName: 'LastName',
							message: "'Last Name' should not be empty.",
						},
						{
							fieldName: 'Email',
						},
						{
							fieldName: 'Phone',
						},
						{
							fieldName: 'DateOfBirth',
						},
					],
					meta: null,
				},
			},
			{ status: 400 }
		);
	}

	if (firstName === '401user') {
		return HttpResponse.json(
			{},

			{ status: 401 }
		);
	}

	if (mockSettings.showAPIGenericErrorOnRoleSubmitWithLogin) {
		return HttpResponse.json({}, { status: 500 });
	}

	if (firstName === '500user') {
		return HttpResponse.json({}, { status: 500 });
	}

	if (
		response.status === 'valid' &&
		userId === '' &&
		taxIdNumber === '' &&
		stepOneToken === ''
	) {
		if (
			(checkDOB && request.dateOfBirth !== validDateOfBirth) ||
			(checkZipCode && request.zip !== validZipCode)
		) {
			response.eligibilityStatusCode = 'other';
			response.status = 'other';
			response.token = null;
		}
	}

	return HttpResponse.json(response);
}

/**
 * Array of mock handlers that service GraphQL requests from this project.
 */
export const handlers = [
	http.post('*/authorizationcode', async ({ request }) => {
		await delay(dynamicDelay());
		const form = await request.formData();

		const username = form.get('username');
		const password = form.get('password');

		if (mockSettings.showMfaOnboarding) {
			return HttpResponse.json({
				status: 'enforcemfaonboard',
				token: 'tokenContent',
				// This is an optional prop, it indicates that the user is allowed to skip adding an MFA method
				code: 'codeContent',
				redirectUri: 'https://httpbin.org/post',
			});
		}

		let statusCode = 400;
		let response = {
			responseStatus: {
				errorCode: 'LoginInvalid',
				message: '',
			},
		};

		// TODO Convert these to use `mockSettings` instead of magic strings, or write `play` functions for the stories so viewers don't have to know the magic strings
		// user has met password and security question criterias
		if (username === 'validuser' && password === 'validpassword') {
			statusCode = 200;
			if (mockSettings.mfaEnabled) {
				response = { token: 'token.to.mfa', status: 'EnforceMFA' };
			} else {
				response = {
					code: '1becd5e91bb2fdb4bc0f77dc4dc708ba',
					redirectUri: 'https://httpbin.org/post',
					status: 'login',
				};
			}
		}
		// enforce password page
		else if (username === 'changepassword') {
			statusCode = 200;
			response = {
				status: 'enforceStrongPassword',
				token: 'token.to.change.password.upon.login',
			};
		}
		// enforce security question page
		else if (username === 'enforcesq') {
			statusCode = 200;
			response = {
				status: 'enforceSecurityQuestion',
				token: 'token.to.change.securityquestion.upon.login',
			};
		} else if (username === '503user') {
			statusCode = 503;
		} else if (username === 'brokenuser') {
			statusCode = 500;
		} else if (username === 'manytimesuser') {
			response.responseStatus.errorCode = 'LockedOutUser';
		} else if (username === 'wentwronguser') {
			response.responseStatus.errorCode = 'Non-recognized-error';
		} else if (username === 'noteligible') {
			response.responseStatus.errorCode = 'NotEligible';
		} else {
			response.responseStatus.errorCode = 'InvalidLogin';
		}

		return HttpResponse.json(response, { status: statusCode });
	}),

	http.get('*/api/accounts/passwordRules', async () => {
		if (mockSettings.passwordRulesServerError) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		return HttpResponse.json({ passwordRules: passwordRulesMockData });
	}),

	http.post('*/api/accounts/PasswordValidation', async ({ request }) => {
		await delay(dynamicDelay());
		if (mockSettings.invalidPassword) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		const payload = await request.json();
		const errorResponse = {
			responseStatus: {
				errorCode: 'ArgumentException',
				message: 'Invalid Request',
				stackTrace: null,
				errors: [],
				meta: null,
			},
		};
		if (!/[0-9]/.test(payload.password.value)) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'Numeric',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (!/[!@#$%]/.test(payload.password.value)) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'SpecialChar',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (payload.password.value.length < 8) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'Length',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (!/[a-z]/.test(payload.password.value)) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'LowerCase',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (!/[A-Z]/.test(payload.password.value)) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'UpperCase',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (payload.password.value === 'aaaaa') {
			errorResponse.responseStatus.errors.push({
				errorCode: 'CharacterRepeat',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (payload.password.value === 'match') {
			errorResponse.responseStatus.errors.push({
				errorCode: 'AllowUsernameMatch',
				fieldName: 'Password',
				message: 'Please enter a valid password.',
			});
		}
		if (payload.password.value === 'Test@123') {
			return HttpResponse.json({}, { status: 200 });
		}
		return HttpResponse.json(errorResponse, { status: 400 });
	}),

	http.put('*/api/Enforce/Password', async ({ request }) => {
		await delay(dynamicDelay());
		const {
			enforceStrongPassword: { currentPassword },
		} = await request.json();

		if (currentPassword !== 'Test@123') {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'Token',
						message: 'Oops! Invalid Token.',
						errors: [
							{
								errorCode: 'Token',
								fieldName: 'EnforceStrongPassword.Token',
								message: 'Oops! Invalid Token.',
							},
						],
					},
				},
				{
					status: 400,
				}
			);
		}
		return HttpResponse.json({
			enforceStrongPassword: {
				// code: 'caskdnaslfbalskfnalskdnaslkdn',
				// redirect_uri: 'https://dev.empowered-decisions.com/identity/callback',
				// token: 'token.to.send.email',
				// status: 'loginWithEnforcePassword',
				token: 'token.toLogin',
				status: 'login',
			},
		});
	}),

	http.post('*/api/V3/accounts/changePassword', async ({ request }) => {
		const {
			changePassword: { currentPassword },
		} = await request.json();
		await delay(dynamicDelay());

		if (currentPassword !== 'Test@123') {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'Token',
						message: 'Oops! Invalid Token.',
						errors: [
							{
								errorCode: 'Token',
								fieldName: 'EnforceStrongPassword.Token',
								message: 'Oops! Invalid Token.',
							},
						],
					},
				},
				{
					status: 400,
				}
			);
		}

		return HttpResponse.json({
			changePassword: {
				token: 'token.toChange.Password',
				status: 'changePassword',
			},
		});
	}),

	http.post('*/api/forgotUsername', async () => {
		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		await delay(dynamicDelay());

		if (mockSettings.memberNotFound) {
			return HttpResponse.json({
				status: 'invalid',
			});
		}

		if (mockSettings.invalidUserFields) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'ArgumentException',
						message: 'Invalid Request',
						stackTrace: null,
						errors: [
							{
								errorCode: 'InValid',
								fieldName: 'dateOfBirth',
								message: 'dateofBirth is invalid',
							},
							{
								errorCode: 'InValid',
								fieldName: 'redirect_uri',
								message: 'redirect_uri is invalid',
							},
							{
								errorCode: 'InValid',
								fieldName: 'Email',
								message: 'email is invalid',
							},
						],
						meta: null,
					},
				},
				{ status: 400 }
			);
		}
		return HttpResponse.json({
			status: 'valid',
		});
	}),

	http.get('*/api/V2/accounts/securityQuestions', async () => {
		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		return HttpResponse.json({ securityQuestions: securityQuestionsMockData });
	}),

	http.get('*/api/V2/accounts/securityQuestions', async () => {
		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		return HttpResponse.json({ securityQuestions: securityQuestionsMockData });
	}),

	http.put('*/api/Enforce/SecurityQuestion', async ({ request }) => {
		const {
			enforceSecurityQuestion: { answer },
		} = await request.json();
		await delay(dynamicDelay());
		if (answer !== 'agile') {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'Token',
						message: 'Oops! Invalid Token.',
						stackTrace: null,
						errors: [
							{
								errorCode: 'Token',
								fieldName: 'token',
								message: 'Oops! Invalid Token.',
							},
							{
								errorCode: 'InValid',
								fieldName: 'dateOfBirth',
								message: 'dateofBirth is invalid',
							},
							{
								errorCode: 'InValid',
								fieldName: 'redirect_uri',
								message: 'redirect_uri is invalid',
							},
							{
								errorCode: 'InValid',
								fieldName: 'email',
								message: 'email is invalid',
							},
						],
						meta: null,
					},
				},
				{ status: 400 }
			);
		}

		return HttpResponse.json({
			enforceSecurityQuestion: {
				id: '22',
				code: 'caskdnaslfbalskfnalskdnaslkdn',
				redirectUri: 'https://dev.empowered-decisions.com/identity/callback',
				token: 'token.to.send.email',
				status: 'loginWithEnforceSecurityQuestion',
			},
		});
	}),
	/**
	 * REQUEST BODY
	 * {
	 * 		userId,
	 * 		password,
	 * 		email,
	 * 		pin,
	 * 		taxIdNumber,
	 * 		facilityId,
	 * 		npi1,
	 * 		client_id,
	 * 		redirect_uri,
	 * 		blob,
	 * 		token,
	 * 		status,
	 * 		registrationUserType, // "ClinicalUser" | "FitnessUser" | "ExistingUser" | "RegistrationToken"
	 * 		registrationToken,
	 * }
	 */
	http.post('*/api/userVerification', async ({ request }) => {
		const body = await request.json();
		if (mockSettings.registrationTokenIsExpired) {
			return HttpResponse.json({
				memberId: 1234567,
				status: 'invalid',
				invalidCode: 'expiredToken',
				memberInfo: {
					groupNumber: 123,
					planId: 1123,
					effectiveDate: '2025-01-01',
					suffix: '00',
					gender: 'U',
					eligId: 'eligid',
				},
				registrationToken: 'regToken',
				registrationUserType: 'ClinicalUser || FitnessUser',
				displayInfo: {
					location: "Bob's Gym",
					address: '1124 Oak St. San Diego, CA 92127',
					name: 'John Smith',
				},
				email: 'string',
				isPrimary: true,
			});
		}
		// TODO Remove this once we migrate everything to the new handler
		if (
			!body.registrationUserType ||
			mockSettings.useLegacyUserVerificationHandler
		)
			return legacyUserVerificationHandler(request, body);
		const { email, npi1, registrationUserType } = body;

		// Updated handler based on [userVerification API contract](https://dev.azure.com/ashcompanies/WellnessTaskForce/_wiki/wikis/WellnessTaskForce.wiki/13581/userVerification)
		await delay(dynamicDelay());
		if (mockSettings.returnError) {
			return HttpResponse.json({}, { status: mockSettings.returnErrorCode });
		}

		// Validation returns
		if (mockSettings.registrationLegacyUser) {
			return HttpResponse.json({
				status: 'LegacyUser',
			});
		}
		if (mockSettings.registrationRequireNpi && !npi1) {
			return HttpResponse.json({
				status: 'RequiredMoreInfo',
				token: 'RequiredMoreInfoToken',
			});
		}
		if (mockSettings.returnPrimaryAccountError) {
			return HttpResponse.json({
				status: 'invalid',
				invalidCode: 'PrimaryAccountNotRegistered',
			});
		}
		if (mockSettings.registrationEmailInvalid) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'ArgumentException',
						message: 'Invalid Request',
						errors: [
							{
								errorCode: 'InValid',
								fieldName: 'Email',
								message: "'Email' should not be empty or invalid.",
							},
						],
					},
				},
				{ status: 400 }
			);
		}
		if (mockSettings.registrationTaxIdInvalid) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'ArgumentException',
						message: 'Invalid Request',
						errors: [
							{
								errorCode: 'InValid',
								// BE is sending this now, but it should be 'TaxIdNumber'
								fieldName: 'TaxId',
								// This is what we would like BE to send, so it matches the request
								// fieldName: 'TaxIdNumber',
								message: "'TaxId' should not be empty or invalid.",
							},
						],
					},
				},
				{ status: 400 }
			);
		}
		// Happy path
		if (registrationUserType === 'RegistrationToken') {
			return HttpResponse.json({
				redirectUri: '',
				/**
				 * Auth code for logging in
				 */
				code: '',
			});
		}
		const displayInfo =
			registrationUserType === 'ClinicalUser'
				? [{ name: 'John Smith' }]
				: [
						{ location: "Bob's Gym" },
						{ address: '1124 Oak St. San Diego, CA 92127' },
				  ];
		return HttpResponse.json({
			token: 'validTokenFromUserVerification',
			status: 'valid',
			email,
			displayInfo,
			/**
			 * 'ClinicalUser' | 'FitnessUser'
			 */
			registrationUserType,
			/**
			 * Nullable boolean
			 */
			isPrimary: true,
		});
	}),

	http.get('*/api/accounts/usernameValidationRules', async () => {
		if (mockSettings.returnError) {
			return new HttpResponse(null, { status: 500 });
		}
		await delay(dynamicDelay());
		return HttpResponse.json(usernameRulesMockData);
	}),

	http.post('*/api/accounts/usernamevalidation', async ({ request }) => {
		const payload = await request.json();
		const errorResponse = {
			responseStatus: {
				errorCode: 'ArgumentException',
				message: 'Invalid Request',
				stackTrace: null,
				errors: [],
				meta: null,
			},
		};

		if (payload.username.value.length < 5) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'MinimumLength',
				fieldName: 'Username.Value',
				message: 'Oops! Username does not meet requirements.',
			});
		}
		if (payload.username.value.length > 15) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'MaximumLength',
				fieldName: 'Username.Value',
				message: 'Oops! Username does not meet requirements.',
			});
		}
		if (payload.username.value === 'notunique') {
			errorResponse.responseStatus.errors.push({
				errorCode: 'Unique',
				fieldName: 'Username.Value',
				message: 'Oops! Username does not meet requirements.',
			});
		}
		if (!/^[\w\d]+$/.test(payload.username.value)) {
			errorResponse.responseStatus.errors.push({
				errorCode: 'Alphanumeric',
				fieldName: 'Username.Value',
				message: 'Oops! Username does not meet requirements.',
			});
		}

		if (errorResponse.responseStatus.errors.length === 0) {
			return HttpResponse.json({}, { status: 200 });
		}
		await delay(dynamicDelay());
		return HttpResponse.json(errorResponse, { status: 400 });
	}),

	http.post('*/api/UserRegistration', async ({ request }) => {
		const payload = await request.json();
		await delay(dynamicDelay());
		if (mockSettings.showMfaOnboarding) {
			return HttpResponse.json({
				status: 'enforcemfaonboard',
				token: 'tokenContent',
				// This is an optional prop, it indicates that the user is allowed to skip adding an MFA method
				code: 'codeContent',
				redirectUri: 'https://httpbin.org/post',
			});
		}
		if (payload.username.value === '400User') {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'NotEmpty',
						message: "'Client Id' should not be empty.",
						stackTrace: null,
						errors: [
							{
								errorCode: 'NotEmpty',
								fieldName: 'ClientId',
								message: "'Client Id' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'RedirectUri',
								message: "'Redirect Uri' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'SecurityAnswer',
								message: "'Redirect Uri' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'SecurityQuestion',
								message: "'Redirect Uri' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'Password',
								message: "'Redirect Uri' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'Email',
								message: "'Redirect Uri' should not be empty.",
							},
							{
								errorCode: 'NotEmpty',
								fieldName: 'ConfirmPassword',
								message: "'Redirect Uri' should not be empty.",
							},
						],
						meta: null,
					},
				},
				{ status: 400 }
			);
		}

		if (payload.username.value === '401User')
			return HttpResponse.json({}, { status: 401 });

		if (payload.username.value === '500User')
			return HttpResponse.json({}, { status: 500 });

		if (payload.username.value === '400Username') {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'ArgumentException',
						message: 'Invalid Request',
						errors: [
							{
								errorCode: 'Invalid',
								fieldName: 'Username',
								message: 'Oops! That username is not available.',
							},
						],
					},
				},
				{ status: 400 }
			);
		}
		// successful registration response
		return HttpResponse.json({
			redirectUri:
				'https://dev.empowered-decisions.com/auth/AuthorizationCodeProvider',
			code: '91bae739b2813da94b369f89967f9122',
		});
	}),

	http.get('*/api/accounts/email', async () => {
		await delay(dynamicDelay());
		if (mockSettings.returnError) {
			return new HttpResponse(null, { status: 500 });
		}
		return HttpResponse.json({
			userEmail: { id: 1712235234, email: 'response@ashn.com' },
		});
	}),
	/**
	 * The first step in the 'forgot' process where the user requests an email with a reset link
	 * Request:
	 * {
	 *   "resetPassword": {
	 *     "client_id": string
	 *     "redirectUri": string
	 *     "username": string
	 *   }
	 * }
	 */
	http.post('*/api/forgotPassword', async ({ request }) => {
		const body = await request.json();

		const requiredFields = ['client_id', 'redirectUri', 'username'];
		requiredFields.forEach(field => {
			if (!body?.resetPassword?.[field]) {
				throw new Error(
					`Invalid request body for /forgotPassword/ missing ${field}`
				);
			}
		});

		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 400,
			});
		}
		await delay(dynamicDelay());
		return new HttpResponse(null, { status: 204 });
	}),
	/**
	 * Fetches the security question for the user to answer.
	 */
	http.get('*/api/forgotPassword/question', async ({ request }) => {
		await delay(dynamicDelay());
		if (!request.headers.get('AshToken')) {
			return new HttpResponse(null, {
				status: 401,
			});
		}
		if (mockSettings.expiredToken) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'TokenException',
						message: 'Oops! Invalid Token.',
						errors: [],
					},
				},
				{
					status: 401,
				}
			);
		}
		if (mockSettings.errorFetchingSecurityQuestion) {
			return new HttpResponse(null, {
				status: 400,
			});
		}
		return HttpResponse.json({
			securityQuestion: [
				{
					question: 'What is the airspeed velocity of an unladen swallow?',
					id: 0,
				},
			],
		});
	}),
	/**
	 * Verifies the user's response to the security question.
	 */
	http.post('*/api/forgotPassword/verify', async ({ request }) => {
		await delay(dynamicDelay());
		if (!request.headers.get('AshToken') || mockSettings.expiredToken) {
			return new HttpResponse(null, {
				status: 401,
			});
		}
		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 500,
			});
		}
		if (mockSettings.wrongSecurityAnswer) {
			// Allow the next request to succeed
			mockSettings.wrongSecurityAnswer = false;
			return HttpResponse.json({
				answer: {
					isAnswerValidated: false,
					isMaximumAttemptReached: false,
					message: 'Oops! Answer is incorrect.',
					token: 'tokenFromWrongAnswer',
				},
			});
		}
		if (mockSettings.maximumAttemptReached) {
			return HttpResponse.json({
				answer: {
					isAnswerValidated: false,
					isMaximumAttemptReached: true,
					message: 'Maximum Attempts Reached',
				},
			});
		}
		const body = await request.json();
		// Verify the request shape matches
		if (!body?.answer?.memberSecurityAnswers?.[0]?.comparisonAnswer)
			throw new Error('Invalid request body for /forgotPassword/verify');

		return HttpResponse.json({
			answer: {
				token: 'tokenFromVerify',
				isAnswerValidated: true,
				isMaximumAttemptReached: false,
			},
		});
	}),

	http.post('*/api/forgotPassword/confirm', async ({ request }) => {
		await delay(dynamicDelay());
		if (!request.headers.get('AshToken')) {
			return new HttpResponse(null, {
				status: 401,
			});
		}
		if (mockSettings.expiredToken) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'Token',
						message: 'Oops! Invalid Token.',
						errors: [
							{
								errorCode: 'Token',
								fieldName: 'EnforceStrongPassword.Token',
								message: 'Oops! Invalid Token.',
							},
						],
					},
				},
				{ status: 401 }
			);
		}
		if (mockSettings.returnError) {
			return new HttpResponse(null, {
				status: 400,
			});
		}
		const body = await request.json();
		// Verify the request shape matches
		if (!body?.confirmation?.newPassword || !body?.confirmation?.confirm)
			throw new Error('Invalid request body for /forgotPassword/confirm');

		return new HttpResponse(null, { status: 200 });
	}),

	// get user's email verification status
	http.get('*/api/Email/Status', async () => {
		await delay(dynamicDelay());
		if (mockSettings.returnError) {
			return new HttpResponse(null, { status: 500 });
		}
		if (mockSettings.emailNotVerified) {
			return HttpResponse.json({
				email: 'response@ashn.com',
				verified: false,
				enabled: true,
				mfaEnabled: mockSettings.mfaEnabled,
			});
		}
		return HttpResponse.json({
			email: 'response@ashn.com',
			verified: true,
			enabled: true,
			mfaEnabled: mockSettings.mfaEnabled,
		});
	}),

	// handle resending verification email
	http.post('*/api/Email/Resend', () => {
		const { resendEmailError } = mockSettings;

		if (resendEmailError) {
			return new HttpResponse(null, { status: 400 });
		}
		return new HttpResponse(null, { status: 204 });
	}),

	// handle resending verification email
	http.get('*/api/Email/Verify', async ({ request }) => {
		await delay(dynamicDelay());
		if (!request.headers.get('AshToken') || mockSettings.expiredToken) {
			return new HttpResponse(null, {
				status: 401,
			});
		}
		return new HttpResponse(null, { status: 204 });
	}),

	// submit change email
	http.put('*/api/accounts/email', async ({ request }) => {
		const { incorrectPassword } = mockSettings;

		if (incorrectPassword) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'ArgumentException',
						message: 'Invalid Request',
						errors: [
							{
								errorCode: 'InValid',
								fieldName: 'changeEmail.password',
								message: 'Oops! Password is incorrect.',
							},
						],
					},
				},
				{ status: 400 }
			);
		}
		await delay(dynamicDelay());
		const { changeEmail } = await request.json();

		return HttpResponse.json(changeEmail);
	}),

	// submit change security question
	http.post('*/api/V3/accounts/changeSecurityQuestion', async ({ request }) => {
		const {
			memberSecurityQuestion: { password },
		} = await request.json();
		await delay(dynamicDelay());
		if (password === 'validpassword') {
			return new HttpResponse({}, { status: 200 });
		}
		return HttpResponse.json(
			{
				responseStatus: {
					errorCode: 'ArgumentException',
					message: 'Invalid Request',
					errors: [
						{
							errorCode: 'InValid',
							fieldName: 'MemberSecurityQuestion.Password',
							message: 'Password is incorrect.',
						},
					],
				},
			},
			{ status: 400 }
		);
	}),

	http.get('*/api/Verification/SendOtp', async ({ request }) => {
		const url = new URL(request.url);
		const params = new URLSearchParams(url.search);
		const token = params.get('token');
		const medium = params.get('medium');

		if (mockSettings.codeSendError) {
			return new HttpResponse(null, { status: 503 }); // 503 Service Unavailable
		}

		// Validate the query parameters
		if (!token || !medium) {
			return HttpResponse.json(
				{ error: 'Missing token or medium query parameter' },
				{ status: 400 }
			);
		}

		// Example conditions to simulate different responses
		if (token === 'invalid.token') {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'User or Site does not have MFA enabled',
					},
				},
				{ status: 400 }
			);
		}

		if (token === 'recent.request') {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'Previous request was too recent',
					},
				},
				{ status: 400 }
			);
		}

		return HttpResponse.json({ sendRateLimit: 3 }); // small interval for testing
	}),
	http.get('*/api/Enforce/SendOtp', async ({ request }) => {
		const url = new URL(request.url);
		const params = new URLSearchParams(url.search);
		const token = params.get('token');
		const medium = params.get('medium');

		await delay(dynamicDelay());
		if (mockSettings.codeSendError) {
			return new HttpResponse(null, { status: 503 }); // 503 Service Unavailable
		}
		if (mockSettings.returnMfaTooManyCodes) {
			return HttpResponse.json(
				{
					responseStatus: {
						errorCode: 'TooManyRequests',
						message: 'Previous request was too recent',
						errors: [],
					},
				},
				{ status: 400 }
			);
		}

		// Validate the query parameters
		if (!token || !medium) {
			return HttpResponse.json(
				{ error: 'Missing token or medium query parameter' },
				{ status: 400 }
			);
		}

		// Example conditions to simulate different responses
		if (token === 'invalid.token') {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'User or Site does not have MFA enabled',
					},
				},
				{ status: 400 }
			);
		}

		if (token === 'recent.request') {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'Previous request was too recent',
					},
				},
				{ status: 400 }
			);
		}

		return HttpResponse.json({ sendRateLimit: 3 }); // small interval for testing
	}),
	http.post('*/api/Enforce/ValidateOtp', async ({ request }) => {
		const { token, oneTimePassword, medium } = await request.json();

		await delay(dynamicDelay());
		if (mockSettings.returnError) {
			return new HttpResponse(null, { status: 503 });
		}

		if (mockSettings.otpValidationError) {
			return HttpResponse.json({
				validateMfaResponse: {
					id: '1234', // Randomly generated id
					token: 'renewed.token', // Renewed token
					redirect_uri: 'https://callback.uri', // Callback URI
					status: 'EnforceMfa', // Indicates successful login
					application: 'exampleApp', // The app being attempted to log into
				},
			});
		}

		// Validate the request body
		if (!token || !oneTimePassword || !medium) {
			return HttpResponse.json(
				{ error: 'Missing token, oneTimePassword, or medium in request body' },
				{ status: 400 }
			);
		}

		// Example conditions to simulate different responses
		if (token === 'invalid.token') {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'User or Site does not have MFA enabled',
					},
				},
				{ status: 400 }
			);
		}

		if (mockSettings.isMFAFinalAttempt) {
			return HttpResponse.json({
				validateMfaResponse: {
					token: 'renewed.token', // Renewed token
					status: 'EnforceMfa', // indicates successful login
					isFinalAttempt: true, // indicates that the next failed attempt from the user will result in lockout
				},
			});
		}
		if (mockSettings.isMFALockoutUser) {
			return HttpResponse.json(
				{
					responseStatus: {
						message: 'Maximum number of attempts exceeded',
					},
				},
				{ status: 429 }
			);
		}
		// Simulate successful auth
		return HttpResponse.json({
			validateMfaResponse: {
				id: Math.floor(Math.random() * 1000),
				code: 'codeFromEnforceValidateOtp',
				redirect_uri: 'https://httpbin.org/post',
				status: 'Login',
				application: 'exampleApp',
			},
		});
	}),

	http.get('*/api/Enforce/MfaOptions', async () => {
		if (mockSettings.returnError) {
			return new HttpResponse(null, { status: 503 });
		}
		await delay(dynamicDelay());
		if (!mockSettings.mfaOptionsCount) {
			return HttpResponse.json({});
		}

		if (mockSettings.mfaOptionsCount === 0) {
			return HttpResponse.json({
				mfaOptions: [],
			});
		}
		if (mockSettings.mfaOptionsCount === 2) {
			return HttpResponse.json({
				mfaOptions: [
					{
						address: 'test@ashn.com',
						medium: 'Email',
					},
					{
						address: '555-555-1234',
						medium: 'SMS',
					},
				],
			});
		}

		if (mockSettings.mfaOptionsCount === 3) {
			return HttpResponse.json({
				mfaOptions: [
					{
						address: 'test@ashn.com',
						medium: 'Email',
					},
					{
						address: '555-555-1234',
						medium: 'SMS',
					},
					{
						address: '555-555-1234',
						medium: 'SMS',
					},
				],
			});
		}

		return HttpResponse.json({
			mfaOptions: [
				{
					address: 'test@ashn.com',
					medium: 'Email',
				},
			],
		});
	}),
	...mfaPhoneNumberHandlers,
];
